import { useEffect, useState } from 'react';
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import TenantList from './components/TenantList';
import TenantForm from './components/TenantForm';
import SupportTicketList from './components/SupportTicketList';
import SupportTicketDetail from './components/SupportTicketDetail';
import { useDisplayName } from './hooks/useDisplayName';
import { configureAmplify } from './amplify-config';
import type { Tenant } from '@policy-tracker/types';

function App() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        configureAmplify()
            .then(() => setLoading(false))
            .catch((err: unknown) => {
                const message = err instanceof Error ? err.message : 'An unexpected error occurred';
                setError(message);
                setLoading(false);
            });
    }, []);

    if (loading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div>
                <h1>Failed to Load</h1>
                <p>The application could not be configured. Please try refreshing the page.</p>
                <p>{error}</p>
            </div>
        );
    }

    return (
        <Authenticator hideSignUp>
            <AppContent />
        </Authenticator>
    );
}

/**
 * Top-level view state for the TM admin app. Three primary screens:
 *
 *   `tenants`        — TenantList / TenantForm (legacy).
 *   `support-list`   — SupportTicketList (#2304 part 2).
 *   `support-detail` — SupportTicketDetail (#2304 part 2).
 *
 * Browser URL is kept in sync via `history.pushState` so refresh + back
 * button behave naturally. `popstate` re-derives the state from
 * `window.location.pathname` (see `seedFromPath`).
 */
type ViewState = { view: 'tenants' } | { view: 'support-list' } | { view: 'support-detail'; tenantId: string; ticketId: string };

function seedFromPath(path: string): ViewState {
    if (path.startsWith('/admin/support/')) {
        const parts = path.slice('/admin/support/'.length).split('/').filter(Boolean);
        if (parts.length >= 2) {
            return { view: 'support-detail', tenantId: parts[0]!, ticketId: parts[1]! };
        }
        return { view: 'support-list' };
    }
    if (path === '/admin/support') {
        return { view: 'support-list' };
    }
    return { view: 'tenants' };
}

function viewToPath(state: ViewState): string {
    switch (state.view) {
        case 'support-list':
            return '/admin/support';
        case 'support-detail':
            return `/admin/support/${state.tenantId}/${state.ticketId}`;
        case 'tenants':
        default:
            return '/admin';
    }
}

function AppContent() {
    const { user, signOut, authStatus } = useAuthenticator((context) => [context.user, context.authStatus]);
    const displayName = useDisplayName(user?.username ?? '');
    const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
    const [viewState, setViewState] = useState<ViewState>(() => (typeof window === 'undefined' ? { view: 'tenants' } : seedFromPath(window.location.pathname)));

    useEffect(() => {
        const onPopState = () => {
            setViewState(seedFromPath(window.location.pathname));
        };
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, []);

    const goTo = (next: ViewState) => {
        setViewState(next);
        window.history.pushState({}, '', viewToPath(next));
    };

    if (authStatus === 'configuring') {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading...</p>
            </div>
        );
    }

    const handleEdit = (tenant: Tenant) => setEditingTenant(tenant);
    const handleEditSuccess = () => setEditingTenant(null);
    const handleEditCancel = () => setEditingTenant(null);

    return (
        <div className="app">
            <header className="header">
                <div className="container">
                    <div className="header-nav">
                        <h1>Tenant Management</h1>
                        <nav style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                                className="btn btn-outline"
                                onClick={() => {
                                    setEditingTenant(null);
                                    goTo({ view: 'tenants' });
                                }}
                                aria-pressed={viewState.view === 'tenants'}
                            >
                                Tenants
                            </button>
                            <button
                                className="btn btn-outline"
                                onClick={() => goTo({ view: 'support-list' })}
                                aria-pressed={viewState.view === 'support-list' || viewState.view === 'support-detail'}
                            >
                                Support
                            </button>
                        </nav>
                        <div>
                            <span>Welcome, {displayName}</span>
                            <button onClick={signOut} className="btn btn-outline" style={{ marginLeft: '1rem' }}>
                                Sign Out
                            </button>
                        </div>
                    </div>
                </div>
            </header>
            <main className="container">
                {viewState.view === 'tenants' &&
                    (editingTenant ? (
                        <div className="card">
                            <h2>Edit Tenant</h2>
                            <TenantForm tenant={editingTenant} onSuccess={handleEditSuccess} onCancel={handleEditCancel} />
                        </div>
                    ) : (
                        <TenantList onEdit={handleEdit} />
                    ))}
                {viewState.view === 'support-list' && (
                    <SupportTicketList onSelect={(tenantId, ticketId) => goTo({ view: 'support-detail', tenantId, ticketId })} />
                )}
                {viewState.view === 'support-detail' && (
                    <SupportTicketDetail tenantId={viewState.tenantId} ticketId={viewState.ticketId} onBack={() => goTo({ view: 'support-list' })} />
                )}
            </main>
        </div>
    );
}

export default App;
